/*
 * Rive illustrations for the About template.
 *
 * The runtime is heavy — roughly 815KB gzipped across the script and its wasm —
 * so nothing is fetched until an animation is actually near the viewport, and
 * only ever once per page however many instances there are.
 *
 * The wasm is served from the theme's own assets. Left alone the runtime pulls
 * it from a public CDN, which would put a third-party request on the storefront.
 */

/** @type {Promise<any> | null} Shared across every instance on the page. */
let runtime = null;

/**
 * Load the Rive runtime once, pointed at the theme's own wasm.
 *
 * @param {string} runtimeUrl
 * @param {string} wasmUrl
 * @returns {Promise<any>}
 */
function loadRuntime(runtimeUrl, wasmUrl) {
	if (runtime) return runtime;

	runtime = new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = runtimeUrl;
		script.async = true;
		script.onload = () => {
			if (!window.rive) return reject(new Error('Rive runtime loaded but exposed nothing'));
			window.rive.RuntimeLoader.setWasmUrl(wasmUrl);
			resolve(window.rive);
		};
		script.onerror = () => reject(new Error('Rive runtime failed to load'));
		document.head.appendChild(script);
	});

	return runtime;
}

/** @returns {boolean} */
const prefersReducedMotion = () =>
	window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

customElements.define(
	'about-rive',
	class extends HTMLElement {
		/** @type {any} */
		#animation = null;
		/** @type {IntersectionObserver | null} */
		#observer = null;

		connectedCallback() {
			const canvas = this.querySelector('canvas');
			const src = this.dataset.src;
			if (!canvas || !src) return;

			/* Load on approach, then play and pause with visibility. Rive renders
			   on every frame while playing, so an animation scrolled past would
			   otherwise keep burning frames off screen. */
			this.#observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) this.#start(canvas, src);
					else this.#animation?.pause();
				},
				{ rootMargin: '300px' }
			);

			this.#observer.observe(this);
		}

		disconnectedCallback() {
			this.#observer?.disconnect();
			this.#observer = null;
			this.#animation?.cleanup();
			this.#animation = null;
		}

		/**
		 * @param {HTMLCanvasElement} canvas
		 * @param {string} src
		 */
		async #start(canvas, src) {
			if (this.#animation) {
				this.#animation.play();
				return;
			}

			/* Claim the slot before awaiting, so a second intersection while the
			   runtime is still in flight cannot build a duplicate instance. */
			this.#animation = true;

			try {
				const rive = await loadRuntime(this.dataset.runtime, this.dataset.wasm);
				if (!this.isConnected) return;

				const stateMachine = this.dataset.stateMachine || undefined;

				this.#animation = new rive.Rive({
					canvas,
					src,
					autoplay: !prefersReducedMotion(),
					stateMachines: stateMachine,
					/* Files authored with data binding carry a view model whose
					   properties drive the state machine — a loop flag, an intro
					   trigger. Nothing binds an instance by default, so those sit
					   unread and the animation behaves as if every flag were off.
					   Harmless on a file that has no view model. */
					autoBind: true,
					layout: new rive.Layout({ fit: rive.Fit.Contain, alignment: rive.Alignment.Center }),
					onLoad: () => {
						this.#animation.resizeDrawingSurfaceToCanvas();

						/* With no state machine named, the runtime falls back to the
						   first linear animation, which in a state-machine file is
						   usually a one-shot intro — it plays once and stops. Prefer
						   the file's own state machine so the merchant doesn't have
						   to know its name. */
						if (!stateMachine && !prefersReducedMotion()) {
							const [first] = this.#animation.stateMachineNames ?? [];
							if (first) this.#animation.play(first);
						}

						this.classList.add('is-ready');
					},
				});
			} catch (error) {
				/* A missing runtime should degrade to empty space, not a broken
				   page — the canvas simply never paints. */
				this.#animation = null;
				console.warn('[about-rive]', error.message);
			}
		}
	}
);
